import React, { useState, useEffect } from 'react';
import { Star, Trash2, RefreshCw, MessageSquare, Search } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const StarRating = ({ rating }) => (
  <div className="admin-star-rating">
    {[1, 2, 3, 4, 5].map(star => (
      <Star key={star} className={star <= rating ? 'is-filled' : ''} />
    ))}
  </div>
);

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean).filter(part => !/^(mr|mrs|miss|ms|mis|dr|prof)\.?$/i.test(part));
  if (!parts.length) return 'A';
  return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('reviews');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = async () => {
    try {
      const [reviewData, restData] = await Promise.all([
        api.getReviews(),
        api.getRestaurant()
      ]);
      setReviews(reviewData);
      setRestaurant(restData);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review? This will update your restaurant rating.')) return;
    try {
      await api.deleteReview(id);
      await load(); // Reload to get updated rating
    } catch (err) {
      alert('Failed to delete review');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  // The review collection is the source of truth. Older restaurant records
  // may still contain the original demo placeholders (4.8 / 1240).
  const totalReviews = reviews.length;
  const avgRating = totalReviews
    ? reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / totalReviews
    : 0;
  const filteredReviews = reviews.filter(review => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || [review.customerName, review.comment].filter(Boolean).join(' ').toLowerCase().includes(query);
    return matchesSearch && (ratingFilter === 'all' || review.rating === Number(ratingFilter));
  });
  const totalPages = Math.max(1, Math.ceil(filteredReviews.length / pageSize));
  const visibleReviews = filteredReviews.slice((page - 1) * pageSize, page * pageSize);
  const ratingCounts = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: reviews.filter(review => review.rating === rating).length,
    percentage: reviews.length ? Math.round((reviews.filter(review => review.rating === rating).length / reviews.length) * 100) : 0,
  }));
  const changePage = (nextPage) => {
    setPage(nextPage);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Reviews</h2>
          <p className="text-gray-500 text-sm">Manage customer reviews</p>
        </div>
        {activeSection === 'reviews' && (
          <div className="admin-toolbar">
            <div className="admin-search-field">
              <Search className="w-4 h-4" />
              <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search reviews..." />
            </div>
            <select value={ratingFilter} onChange={e => { setRatingFilter(e.target.value); setPage(1); }} aria-label="Filter by rating">
              <option value="all">All ratings</option>
              {[5, 4, 3, 2, 1].map(rating => <option key={rating} value={rating}>{rating} stars</option>)}
            </select>
            <span className="admin-result-count">{filteredReviews.length} results</span>
          </div>
        )}
        <button onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div></AdminTopbarPortal>

      <div className="admin-section-tabs" role="tablist" aria-label="Review sections">
        <button type="button" role="tab" aria-selected={activeSection === 'overview'} className={activeSection === 'overview' ? 'is-active' : ''} onClick={() => setActiveSection('overview')}>Overview</button>
        <button type="button" role="tab" aria-selected={activeSection === 'reviews'} className={activeSection === 'reviews' ? 'is-active' : ''} onClick={() => setActiveSection('reviews')}>Reviews</button>
      </div>

      {/* Overall Rating Card */}
      <div className="admin-metric-grid">
        <div><span>Average rating</span><strong>{avgRating.toFixed(1)} / 5</strong><StarRating rating={Math.round(avgRating)} /></div>
        <div><span>Total reviews</span><strong>{totalReviews}</strong></div>
        <div><span>Five-star reviews</span><strong>{reviews.filter(review => review.rating === 5).length}</strong></div>
      </div>

      {activeSection === 'overview' && (
        <div className="admin-review-overview">
          <div>
            <span>Rating distribution</span>
            <strong>How customers rated their orders</strong>
          </div>
          <div className="admin-rating-breakdown">
            {ratingCounts.map(item => (
              <div key={item.rating} className="admin-rating-breakdown-row">
                <span>{item.rating} star</span>
                <div><i style={{ width: `${item.percentage}%` }} /></div>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews List */}
      {activeSection === 'reviews' && (reviews.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">No reviews yet</h3>
          <p className="text-gray-500 text-sm">Customer reviews will appear here</p>
        </div>
      ) : (
        <div className="admin-review-list">
          <div className="admin-review-list-header" aria-hidden="true">
            <span>Customer</span><span>Review</span><span>Date</span><span>Order</span><span>Total</span><span />
          </div>
          {visibleReviews.map(review => (
            <div key={review.id} className="admin-review-row">
              <div className="admin-review-customer" data-label="Customer">
                <div className="admin-person-avatar">{getInitials(review.customerName)}</div>
                <div>
                  <strong>{review.customerName || 'Anonymous'}</strong>
                  <span>Customer review</span>
                </div>
              </div>
              <div className="admin-review-content" data-label="Review">
                <StarRating rating={review.rating} />
                <p>{review.comment || 'No written comment'}</p>
              </div>
              <div className="admin-review-date" data-label="Date">
                <strong>{new Date(review.createdAt || review.orderDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</strong>
              </div>
              <div className="admin-review-order-number" data-label="Order">
                <strong>{review.orderNumber ? `#${review.orderNumber}` : '—'}</strong>
              </div>
              <div className="admin-review-order" data-label="Total">
                <strong>£{Number(review.orderTotal || 0).toFixed(2)}</strong>
              </div>
              <button onClick={() => handleDelete(review.id)} aria-label={`Delete review by ${review.customerName || 'Anonymous'}`}>
                <Trash2 />
              </button>
            </div>
          ))}
        </div>
      ))}
      {activeSection === 'reviews' && filteredReviews.length === 0 && reviews.length > 0 && <div className="admin-empty-state">No reviews match those filters.</div>}
      {activeSection === 'reviews' && totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page === 1} onClick={() => changePage(page - 1)}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => changePage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
